import { Globe, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLocations } from '@/hooks/useLocations';
import { useLocationStore } from '@/store/location';
import { useAuth } from '@/hooks/useAuth';

export interface LocationSelectorProps {
  /** Whether to show "All Locations" option for aggregated views */
  showAllOption?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Style variant */
  variant?: 'header' | 'filter';
  /** Callback when "All Locations" is selected */
  onAllLocationsSelect?: () => void;
  /** Callback when a specific location is selected */
  onLocationSelect?: (locationId: string) => void;
  /** Current value (for controlled mode) - 'all' or location ID */
  value?: string;
  /** Whether the selector is in controlled mode */
  controlled?: boolean;
  /** 
   * Whether to restrict "All Locations" to business owners/superadmins only.
   * When true, managers and supervisors won't see the "All Locations" option.
   * Default: false (all users with multi-location access see the option)
   */
  restrictAllToOwners?: boolean;
}

/**
 * LocationSelector - A reusable component for selecting locations
 *
 * Behavior by role:
 * - Cashiers/Employees: Show read-only badge (locked to device location)
 * - Managers: Show dropdown with assigned locations + optional "All Locations" (unless restricted)
 * - Admins/Owners: Show dropdown with all org locations + optional "All Locations"
 */
export function LocationSelector({
  showAllOption = true,
  className,
  variant = 'header',
  onAllLocationsSelect,
  onLocationSelect,
  value,
  controlled = false,
  restrictAllToOwners = false,
}: LocationSelectorProps) {
  const { locations, isLoading } = useLocations();
  const { currentLocation, setCurrentLocation, locationScope, setLocationScope } = useLocationStore();
  const { profile } = useAuth();

  // Determine if user can switch locations
  const isCashier = profile?.role === 'cashier' || profile?.role === 'employee';
  const hasMultipleLocations = locations.length > 1;
  
  // Determine if user can see "All Locations" option
  // Business owners (superadmin, primary_admin, owner) always can
  // Managers and below only can if restrictAllToOwners is false
  const isBusinessOwner = profile?.role === 'superadmin' || 
                          profile?.role === 'primary_admin' || 
                          profile?.role === 'owner';
  const canSeeAllLocations = showAllOption && hasMultipleLocations && 
                             (!restrictAllToOwners || isBusinessOwner);

  // Handle location change
  const handleChange = (newValue: string) => {
    if (newValue === 'all') {
      if (controlled) {
        onAllLocationsSelect?.();
      } else {
        // In uncontrolled mode, store "all locations" as a separate scope flag
        // (don't clear currentLocation, which is used for location-specific operations elsewhere)
        setLocationScope('all');
        onAllLocationsSelect?.();
      }
    } else {
      const location = locations.find(l => l.id === newValue);
      if (location) {
        if (!controlled) {
          setCurrentLocation(location);
          setLocationScope('single');
        }
        onLocationSelect?.(newValue);
      }
    }
  };

  // Determine current value
  const currentValue = controlled
    ? value
    : (canSeeAllLocations && locationScope === 'all'
        ? 'all'
        : (currentLocation?.id || ''));

  const selectedLocationName =
    currentValue && currentValue !== 'all'
      ? (locations.find(l => l.id === currentValue)?.name || currentLocation?.name)
      : null;

  // Loading state
  if (isLoading) {
    return (
      <Badge variant="secondary" className={cn("gap-1.5 animate-pulse", className)}>
        <MapPin className="h-3 w-3" />
        Loading...
      </Badge>
    );
  }

  // Cashiers or single location: Show read-only badge
  if (isCashier || locations.length <= 1) {
    return (
      <Badge variant="secondary" className={cn("gap-1.5", className)}>
        <MapPin className="h-3 w-3" />
        {currentLocation?.name || locations[0]?.name || 'No Location'}
      </Badge>
    );
  }

  // Admins/Managers: Show dropdown
  return (
    <Select value={currentValue} onValueChange={handleChange}>
      <SelectTrigger
        className={cn(
          "gap-2",
          variant === 'header' ? "w-[180px]" : "w-[200px]",
          className
        )}
      >
        <SelectValue placeholder="Select location">
          {currentValue === 'all' ? (
            <div className="flex items-center gap-2">
              <Globe className="h-3.5 w-3.5" />
              <span>All Locations</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" />
              <span>{selectedLocationName || 'Select location'}</span>
            </div>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {canSeeAllLocations && (
          <SelectItem value="all">
            <div className="flex items-center gap-2">
              <Globe className="h-3.5 w-3.5" />
              <span>All Locations</span>
            </div>
          </SelectItem>
        )}
        {locations.map((loc) => (
          <SelectItem key={loc.id} value={loc.id}>
            <div className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{loc.name}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default LocationSelector;
